import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from '../components/Toast';
import { jsonDeleteResponse, jsonPost } from '../api/jsonRequest';
import { queryKeys } from '../query/queryKeys';
import { buildPollUrl } from '../query/queryParams';
import { usePollQuery } from '../query/usePollQuery';
import {
    FILE_PROCESS_MAX_ATTEMPTS,
    FILE_PROCESS_POLL_INTERVAL_MS,
    refetchIntervalWithActiveProcessing,
} from '../query/queryConfig';
import axios from 'axios';
import { getAuthConfig } from '../mixins/ApiConfig';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function waitForFileProcessed(jobId, config) {
    for (let attempt = 0; attempt < FILE_PROCESS_MAX_ATTEMPTS; attempt++) {
        await sleep(FILE_PROCESS_POLL_INTERVAL_MS);
        const final = await axios.get(`/api/File/GetFileInfo/${jobId}`, config);
        if (final.data.isProcessed == 4) {
            return;
        }
    }
    throw new Error('Timed out waiting for file processing');
}

function invalidateInvoicesAndFiles(queryClient) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all() });
    void queryClient.invalidateQueries({ queryKey: queryKeys.companyFiles.all() });
    void queryClient.invalidateQueries({ queryKey: queryKeys.files.all() });
}

export function useCompanyInvoices({
    companyId,
    customerId,
    startDate,
    endDate,
    page,
    pageSize,
    enabled = true,
    pauseRefetch = false,
}) {
    const url = buildPollUrl(
        '/api/Invoice/GetInvoiceDataforCompany',
        companyId,
        customerId,
        startDate,
        endDate,
        page,
        pageSize,
    );

    return usePollQuery({
        queryKey: queryKeys.invoices.company(companyId, customerId, startDate, endDate, page, pageSize),
        url,
        enabled,
        pauseRefetch,
        refetchInterval: refetchIntervalWithActiveProcessing,
    });
}

export function useCustomerInvoices({
    companyId,
    customerId,
    startDate,
    endDate,
    page,
    pageSize,
    enabled = true,
    pauseRefetch = false,
}) {
    const url = buildPollUrl(
        '/api/Invoice/GetInvoiceDataforCustomer',
        companyId,
        customerId,
        startDate,
        endDate,
        page,
        pageSize,
    );

    return usePollQuery({
        queryKey: queryKeys.invoices.customer(companyId, customerId, startDate, endDate, page, pageSize),
        url,
        enabled,
        pauseRefetch,
        refetchInterval: refetchIntervalWithActiveProcessing,
    });
}

export function useBotmarkInvoices({
    companyId,
    customerId,
    startDate,
    endDate,
    page,
    pageSize,
    enabled = true,
    pauseRefetch = false,
}) {
    const url = buildPollUrl(
        '/api/Invoice/GetInvoiceDataforBotmark',
        companyId,
        customerId,
        startDate,
        endDate,
        page,
        pageSize,
    );

    return usePollQuery({
        queryKey: queryKeys.invoices.botmark(companyId, customerId, startDate, endDate, page, pageSize),
        url,
        enabled,
        pauseRefetch,
        refetchInterval: refetchIntervalWithActiveProcessing,
    });
}

export function useReferenceEstimates({
    companyId,
    customerId,
    enabled = true,
    pauseRefetch = false,
}) {
    const url = buildPollUrl(
        '/api/Invoice/GetReferenceInvoiceData',
        companyId,
        customerId,
    );

    return usePollQuery({
        queryKey: queryKeys.invoices.reference(companyId, customerId),
        url,
        enabled: Boolean(companyId && customerId) && enabled,
        pauseRefetch,
    });
}

export function useCreateInvoice() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ invoiceObject }) => {
            const invoice = await jsonPost('/api/Invoice/PostInvoice', invoiceObject);
            if (invoice.status !== 200) {
                throw new Error('Creation failed');
            }
            return invoice.data;
        },
        onSuccess: (_data, { JobId } = {}) => {
            Toast.fire({
                title: 'Estimate Generated',
                icon: 'success',
            });
            invalidateInvoicesAndFiles(queryClient);
            // Match the old saga: close UI as soon as PostInvoice succeeds; keep polling
            // file completion in the background so plans refresh when processing finishes.
            if (JobId) {
                const config = getAuthConfig();
                void waitForFileProcessed(JobId, config)
                    .then(() => invalidateInvoicesAndFiles(queryClient))
                    .catch((error) => {
                        if (error?.message === 'Timed out waiting for file processing') {
                            invalidateInvoicesAndFiles(queryClient);
                            Toast.fire({
                                title: 'Estimate created, but file processing timed out. Refresh plans shortly.',
                                icon: 'error',
                            });
                        }
                    });
            }
        },
        onError: () => {
            Toast.fire({
                title: 'Creation failed',
                icon: 'error',
            });
        },
    });
}

export function useUpdateInvoiceStatus() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ currentStatus, invoice }) => {
            const res = await jsonPost(
                `/api/Invoice/UpdateInvoiceStatus/${currentStatus}`,
                invoice,
            );
            if (res.status !== 200) {
                throw new Error('Error with Accept/Reject Estimate.');
            }
            return res.data;
        },
        onSuccess: (data, variables) => {
            if (data == 'Out of Sync') {
                Toast.fire({
                    title: 'Data Out of Sync, Needs Refresh',
                    icon: 'warning',
                });
            } else {
                const invoice = variables.invoice;
                if (invoice.isInvoiceAccepted === 1) {
                    Toast.fire({
                        title: 'Estimate Recalled',
                        icon: 'success',
                    });
                } else if (invoice.isInvoiceAccepted === 2) {
                    Toast.fire({
                        title: 'Estimate Accepted',
                        icon: 'success',
                    });
                    variables.handleInvoiceClose?.();
                    if (variables.recall == false) {
                        variables.handleInvoiceClose?.();
                    }
                } else if (invoice.isInvoiceAccepted === 3) {
                    Toast.fire({
                        title: 'Rework Estimate',
                        icon: 'warning',
                    });
                    if (variables.recall == false) {
                        variables.handleInvoiceClose?.();
                    }
                } else if (invoice.isInvoiceAccepted === 5) {
                    Toast.fire({
                        title: 'Estimate Accepted',
                        icon: 'success',
                    });
                    variables.onPaidDownload?.(variables.item);
                } else if (invoice.customerInvoiceStatus === 3) {
                    Toast.fire({
                        title: 'Estimate Accepted',
                        icon: 'success',
                    });
                    variables.onPaidDownload?.(variables.item);
                }
            }
            invalidateInvoicesAndFiles(queryClient);
        },
        onError: () => {
            Toast.fire({
                title: 'Error with Accept/Reject Estimate.',
                icon: 'error',
            });
        },
    });
}

export function useDeleteEstimate() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ estimateId }) => {
            const res = await jsonDeleteResponse(`/api/Invoice/DeleteEstimate/${estimateId}`);
            return res.data;
        },
        onSuccess: () => {
            invalidateInvoicesAndFiles(queryClient);
        },
    });
}
